"use client";

import { usePostHog } from "@posthog/react";
import { useForm } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { parseError } from "evlog";
import { useCallback, useRef } from "react";
import type { z } from "zod";

import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldTitle,
} from "#/components/ui/field";
import { Spinner } from "#/components/ui/spinner";
import { Switch } from "#/components/ui/switch";
import { updateGameplaySettingInputSchema } from "#/lib/playerSettingsSchemas";
import { patchMyPlayerProfileQuery } from "#/lib/queries";
import { updateMyGameplaySetting } from "#/server/updateMyGameplaySetting";

import type { ProfileUserId } from "./-profile.types";

type GameplaySetting = z.infer<typeof updateGameplaySettingInputSchema>["setting"];

function useProfileBooleanForm({
  userId,
  setting,
  initialEnabled,
}: {
  userId: ProfileUserId;
  setting: GameplaySetting;
  initialEnabled: boolean;
}) {
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const router = useRouter();
  const savedValueRef = useRef(initialEnabled);
  const form = useForm({ defaultValues: { enabled: initialEnabled } });

  const persistIfChanged = useCallback(
    async ({
      value,
      fieldApi,
    }: {
      value: boolean;
      fieldApi: { setValue: (value: boolean) => void };
    }) => {
      if (value === savedValueRef.current) return undefined;
      try {
        await updateMyGameplaySetting({ data: { setting, enabled: value } });
        savedValueRef.current = value;
        patchMyPlayerProfileQuery({ queryClient, userId, patch: { [setting]: value } });
        void router.invalidate();
        return undefined;
      } catch (err) {
        fieldApi.setValue(savedValueRef.current);
        const parsed = parseError(err);
        if (parsed.status >= 500) posthog.captureException(err);
        return parsed.message;
      }
    },
    [posthog, queryClient, router, setting, userId],
  );

  return { form, persistIfChanged };
}

function BooleanSettingSwitch({
  field,
  switchId,
  title,
}: {
  field: {
    state: { value: boolean; meta: { isValidating: boolean; errors: ReadonlyArray<unknown> } };
    handleChange: (value: boolean) => void;
  };
  switchId: string;
  title: string;
}) {
  const hasError = field.state.meta.errors.length > 0;
  return (
    <span className="flex shrink-0 items-center gap-2 self-center">
      {field.state.meta.isValidating ? <Spinner className="text-muted-foreground" /> : null}
      <Switch
        id={switchId}
        checked={field.state.value}
        disabled={field.state.meta.isValidating}
        onCheckedChange={(checked) => field.handleChange(checked)}
        aria-label={title}
        aria-invalid={hasError}
      />
    </span>
  );
}

export function ProfileBooleanSetting({
  userId,
  setting,
  title,
  description,
  initialEnabled,
}: {
  userId: ProfileUserId;
  setting: GameplaySetting;
  title: string;
  description: string;
  initialEnabled: boolean;
}) {
  const { form, persistIfChanged } = useProfileBooleanForm({ userId, setting, initialEnabled });

  return (
    <form.Field
      name="enabled"
      validators={{
        onChange: updateGameplaySettingInputSchema.shape.enabled,
        onChangeAsync: persistIfChanged,
      }}
    >
      {(field) => {
        const hasError = field.state.meta.errors.length > 0;
        return (
          <Field
            orientation="horizontal"
            className="items-center justify-between gap-4"
            data-invalid={hasError}
          >
            <FieldContent className="min-w-0 flex-1">
              <FieldTitle>{title}</FieldTitle>
              <FieldDescription>{description}</FieldDescription>
              {hasError ? (
                <FieldError
                  errors={field.state.meta.errors as Array<{ message?: string } | undefined>}
                />
              ) : null}
            </FieldContent>
            <BooleanSettingSwitch field={field} switchId={`profile-${setting}`} title={title} />
          </Field>
        );
      }}
    </form.Field>
  );
}
